import type { ReactNode } from 'react';

/**
 * IconMedallion — the round well that holds one glyph. The fill is what carries
 * selected and confirmed; the glyph inside it stays the same either way.
 *
 * Corpus: `SI:284` (rendered at `:111`) is the 38px role-card
 * well, `#f5f5f7` normally and `#fff` once the card is selected;
 * `PR1:567` (rendered at `:172`) is the 42px raised well on
 * a landing feature tile, white with a shadow; `SI:165` is the
 * 64px green success medallion; `SI:279-283` (rendered at
 * `:110`) is the 20px tick in the corner of a role card.
 *
 * The well's inversion reads backwards until it is seen in place: the
 * unselected card is white so the well is grey, the selected card is
 * green-tinted so the well goes white. The medallion is always the opposite of
 * its card, which is why `selected` is a prop here rather than a fill each
 * caller picks.
 *
 * Divergences, disclosed rather than smoothed over:
 *
 * - **The unselected tick.** SI:281 draws `#fff` on `#e5e5ea` — 1.19:1, a mark
 *   nobody can see. This renders the glyph only when the tick is selected, which
 *   is the picture the corpus actually produces, is the only pairing
 *   `contrast.test.ts` will accept, and leaves the tick's presence rather than
 *   its colour as what separates the two states.
 * - **The neutral fill.** `#e5e5ea` reaches this file as `--rp-border-strong`, a
 *   border token used as a surface. ToggleSwitch misuses `--rp-border-control`
 *   the same way; one token for the neutral off-state surface fixes both, and
 *   raising a token is not part of this change.
 * - **The raised shadow.** PR1:567 is `0 2px 8px rgba(0,0,0,0.07)` and has no
 *   token; `--rp-shadow-card` (`0 1px 4px rgba(0,0,0,0.06)`) is the nearest and
 *   is what ships, so the elevation is a little shallower than the corpus's.
 * - **The margins on the success medallion** (SI:165's `margin: 0 auto 18px`)
 *   are left to the caller, the centring as well as the 18px gap. Outer spacing
 *   is layout, and an `auto` inline margin is not inert: on a flex item it
 *   absorbs the free space and shoves the medallion's siblings to the far end.
 * - **No transition on the well.** SI:284 has none; the 0.15s fade belongs to
 *   the tick (SI:282) and to the card behind it (SI:277).
 *
 * The glyph is a child, never a literal inside this file: every mark a person
 * reads is supplied by the caller.
 */
export interface IconMedallionProps {
  readonly children: ReactNode;
  readonly size?: 'xs' | 'md' | 'lg';
  readonly tone?: 'well' | 'raised' | 'brand' | 'state';
  readonly selected?: boolean;
  readonly decorative?: boolean;
  readonly ariaLabel?: string;
}

export function IconMedallion({
  children,
  size = 'md',
  tone = 'well',
  selected = false,
  decorative = true,
  ariaLabel,
}: IconMedallionProps) {
  // Only the tones whose fill inverts carry the modifier. A `selected` sitting
  // in the DOM of a tone that ignores it reads as a state that does something.
  const inverts = tone === 'well' || tone === 'state';
  const classes = [
    'rp-icon-medallion',
    `rp-icon-medallion--${size}`,
    `rp-icon-medallion--${tone}`,
    inverts && selected ? 'rp-icon-medallion--selected' : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  // `role="img"` with nothing to name it is a violation and not a label, so the
  // role waits for copy to carry it. Blank counts as nothing: a label that came
  // back empty from a translation table would otherwise ship an unnamed image,
  // which axe fails as `role-img-alt`.
  //
  // What it falls back to is a plain span, **not** `aria-hidden` — the caller
  // said this medallion is not decorative, and `aria-hidden` on the root would
  // take the whole subtree with it, including a child that carries its own
  // accessible name. So the glyph degrades to the text it is rather than
  // disappearing on a translation miss.
  const named = !decorative && (ariaLabel ?? '').trim() !== '';
  return (
    <span
      className={classes}
      role={named ? 'img' : undefined}
      aria-label={named ? ariaLabel : undefined}
      aria-hidden={decorative || undefined}
    >
      {tone === 'state' && !selected ? null : children}
    </span>
  );
}
